/**
 * Email store — cable slice.
 */
import {
  flattenSidebarCountsPayload,
  applyFolderAwareThreadChange,
  upsertThreadItem,
  removeThreadItem,
  patchMessageEngagement,
  patchMessageReaders,
  prependAuditEvent,
  applyDraftUpserted,
  applyDraftRemoved,
  applyTagUpserted,
  applyTagRemoved,
  stripTagFromThread,
  stripTagFromThreads,
  applyFolderUpserted,
  applyFolderRemoved,
  routeEmailFrame,
  stripOptimisticComments,
  EMAIL_CABLE_CHANNEL,
} from '@tshmanager/email-shared'

export function attachCable(ctx) {
  // ── ActionCable subscription ──────────────────────────────────────────────

  ctx._subscription = null

  ctx._stripTagFromListAndThread = function _stripTagFromListAndThread(tagId) {
    const id = Number(tagId)
    ctx.emails.value = stripTagFromThreads(ctx.emails.value || [], id)
    if (ctx.thread.value) {
      ctx.thread.value = stripTagFromThread(ctx.thread.value, id)
    }
  }

  ctx.connectActionCable = function connectActionCable(personId) {
    if (ctx._subscription) return // Already connected
    if (personId != null && ctx.currentPersonId) {
      ctx.currentPersonId.value = Number(personId)
    }
    ctx._subscription = ctx.cableSubscribe(
      { channel: EMAIL_CABLE_CHANNEL, person_id: personId },
      {
        received(data) {
          ctx._handleCableEvent(data)
        },
        connected() {
          // noop
        },
        disconnected() {
          // noop
        },
      }
    )
  }

  ctx.disconnectActionCable = function disconnectActionCable() {
    ctx._subscription?.unsubscribe()
    ctx._subscription = null
  }

  ctx._handleCableEvent = function _handleCableEvent(data) {
    const handled = routeEmailFrame(data, {
      onEmailSent: (emailId) => {
        ctx._handleComposeSendCable?.('email_sent', emailId)
      },
      onEmailSendFailed: (emailId) => {
        ctx._handleComposeSendCable?.('email_send_failed', emailId)
      },
      onUnreadCount:    (counts) => {
        // Cable broadcasts all-mailbox totals. When a single mailbox is selected
        // the sidebar is mailbox-scoped — applying the cable payload flashes
        // huge wrong badges until fetchSidebarCounts corrects them. Pull instead.
        if (ctx.activeMailbox.value != null) {
          ctx.fetchSidebarCounts()
          return
        }
        // Replace sidebar counts only — do not invent list meta unread from
        // sidebar inbox (that caused unread > total until the next list fetch).
        const flat = flattenSidebarCountsPayload(counts || {})
        ctx.sidebarCounts.value = flat
      },
      onEmailRowUpdated:(row) => { if (row) ctx._updateEmailRow(row) },
      onEmailRemoved:   (id)     => { ctx._removeEmailRow(id) },
      onThreadUpdated:  (updatedThread) => { if (updatedThread) ctx._applyThreadUpdated(updatedThread) },
      onThreadCreated:  (createdThread) => { if (createdThread) ctx._applyThreadCreated(createdThread); else { ctx.fetchEmails(1); ctx.fetchSidebarCounts() } },
      onThreadRemoved:  (id)     => { ctx._removeEmailRow(id) },
      onThreadItemUpserted: (threadId, item) => {
        if (!ctx.thread.value || ctx.thread.value.id !== threadId) return
        const enriched = ctx._enrichCableItem(item)
        ctx._patchOpenThreadItems(items => {
          let next = upsertThreadItem(items, enriched)
          if (enriched?.type === 'comment') {
            const pid = enriched.data?.person_id ?? enriched.data?.person?.id
            next = stripOptimisticComments(next, pid)
          }
          return next
        })
      },
      onThreadItemRemoved: (threadId, itemType, itemId) => {
        if (!ctx.thread.value || ctx.thread.value.id !== threadId) return
        ctx._patchOpenThreadItems(items => removeThreadItem(items, itemType, itemId))
      },
      onMessageEngagementUpdated: (patch) => {
        if (!ctx.thread.value || ctx.thread.value.id !== patch.thread_id) return
        ctx._patchOpenThreadItems(items => patchMessageEngagement(items, patch))
      },
      onMessageReadersUpdated: (patch) => {
        if (!ctx.thread.value || ctx.thread.value.id !== patch.thread_id) return
        ctx._patchOpenThreadItems(items => patchMessageReaders(items, patch))
      },
      onThreadInfobarUpdated: (threadId, infobar) => {
        if (!ctx.thread.value || ctx.thread.value.id !== threadId) return
        // Shallow-merge so tasks-only payloads (create-from-email) keep people/BC chips.
        ctx.thread.value = {
          ...ctx.thread.value,
          infobar: { ...(ctx.thread.value.infobar || {}), ...(infobar || {}) },
        }
      },
      onAuditEventCreated: (threadId, event) => {
        if (!ctx.thread.value || ctx.thread.value.id !== threadId) return
        ctx.auditEvents.value = prependAuditEvent(ctx.auditEvents.value, event)
      },
      onDraftUpserted: (draft) => { ctx._applyDraftUpserted(draft) },
      onDraftRemoved:  (removedDraftId) => { ctx._applyDraftRemoved(removedDraftId) },
      onTagUpserted: (tag) => {
        if (!tag?.id) return
        ctx.emailTags.value = applyTagUpserted(ctx.emailTags.value || [], tag)
        ctx.fetchSidebarCounts()
      },
      onTagRemoved: (tagId) => {
        if (tagId == null) return
        const id = Number(tagId)
        ctx.emailTags.value = applyTagRemoved(ctx.emailTags.value || [], id)
        ctx._stripTagFromListAndThread(id)
        const c = { ...(ctx.sidebarCounts.value || {}) }
        delete c[`tag_${id}`]
        ctx.sidebarCounts.value = c
        if (ctx.activeFolder.value === `tag_${id}`) ctx.setActiveFolder('inbox')
      },
      onFolderUpserted: (folder) => {
        if (!folder?.id) return
        ctx.emailFolders.value = applyFolderUpserted(ctx.emailFolders.value || [], folder)
        ctx.fetchSidebarCounts()
      },
      onFolderRemoved: (folderId) => {
        if (folderId == null) return
        ctx.emailFolders.value = applyFolderRemoved(ctx.emailFolders.value || [], Number(folderId))
        const c = { ...(ctx.sidebarCounts.value || {}) }
        delete c[`folder_${folderId}`]
        ctx.sidebarCounts.value = c
        if (ctx.activeFolder.value === `folder_${folderId}`) ctx.setActiveFolder('inbox')
      },
    })
    if (!handled && data?.type) {
      console.warn('[emailStore] unhandled cable event', data.type)
    }
  }

  ctx._applyDraftUpserted = function _applyDraftUpserted(draft) {
    if (!draft) return
    if (ctx.activeMailbox.value != null && Number(draft.mailbox_id) !== Number(ctx.activeMailbox.value)) {
      ctx.fetchSidebarCounts()
      return
    }
    if (ctx.activeFolder.value === 'drafts') {
      const row = ctx._formatDraftListRow(draft)
      ctx.emails.value = applyDraftUpserted(ctx.emails.value, row)
      ctx.emailsMeta.value = {
        ...ctx.emailsMeta.value,
        total_count: ctx.emails.value.length,
      }
    }
    ctx.fetchSidebarCounts()
  }

  ctx._applyDraftRemoved = function _applyDraftRemoved(removedDraftId) {
    if (removedDraftId == null) return
    if (ctx.activeFolder.value === 'drafts') {
      ctx.emails.value = applyDraftRemoved(ctx.emails.value, Number(removedDraftId))
      ctx.emailsMeta.value = {
        ...ctx.emailsMeta.value,
        total_count: ctx.emails.value.length,
      }
    }
    ctx.fetchSidebarCounts()
  }

  // Optimistic list upsert for the composing user (cable may lag / miss).
  // Same mailbox/folder filter as cable apply — does not refetch sidebar
  // (autosave already adjusts the drafts badge).
  ctx.upsertDraftInList = function upsertDraftInList(draft) {
    if (!draft?.id) return
    if (ctx.activeMailbox.value != null && Number(draft.mailbox_id) !== Number(ctx.activeMailbox.value)) {
      return
    }
    if (ctx.activeFolder.value !== 'drafts') return
    const row = ctx._formatDraftListRow({
      ...draft,
      updated_at: draft.updated_at || new Date().toISOString(),
      last_activity: draft.last_activity || draft.updated_at || new Date().toISOString(),
      is_draft: true,
    })
    ctx.emails.value = applyDraftUpserted(ctx.emails.value, row)
    ctx.emailsMeta.value = {
      ...ctx.emailsMeta.value,
      total_count: ctx.emails.value.length,
    }
  }

  ctx.removeDraftFromList = function removeDraftFromList(removedDraftId) {
    if (removedDraftId == null || ctx.activeFolder.value !== 'drafts') return
    ctx.emails.value = applyDraftRemoved(ctx.emails.value, Number(removedDraftId))
    ctx.emailsMeta.value = {
      ...ctx.emailsMeta.value,
      total_count: ctx.emails.value.length,
    }
  }

  // Apply a thread_updated broadcast: folder-aware list mutate + open-thread patch.
  ctx._applyThreadUpdated = function _applyThreadUpdated(updated) {
    if (!updated) return
    // Cable uses responsible_person; thread UI also reads responsible
    if (updated.responsible_person && !updated.responsible) {
      updated = { ...updated, responsible: updated.responsible_person }
    }

    ctx.emails.value = applyFolderAwareThreadChange(
      ctx.emails.value,
      updated,
      ctx.activeFolder.value,
      ctx.activeMailbox.value,
    )
    ctx.emails.value = ctx._sortEmailsByActivity(ctx.emails.value)

    // If this thread is currently open, merge Thread fields into thread.value
    if (ctx.thread.value && ctx.thread.value.id === updated.id) {
      ctx.thread.value = { ...ctx.thread.value, ...updated }
    }
  }

  ctx._applyThreadCreated = function _applyThreadCreated(created) {
    if (!created) {
      ctx.fetchEmails(1)
      ctx.fetchSidebarCounts()
      return
    }
    if (created.responsible_person && !created.responsible) {
      created = { ...created, responsible: created.responsible_person }
    }
    const beforeLen = ctx.emails.value.length
    ctx.emails.value = applyFolderAwareThreadChange(
      ctx.emails.value,
      created,
      ctx.activeFolder.value,
      ctx.activeMailbox.value,
    )
    ctx.emails.value = ctx._sortEmailsByActivity(ctx.emails.value)
    if (ctx.emails.value.length === beforeLen && !ctx.emails.value.some(e => e.id === created.id)) {
      // Did not belong — still refresh badges
    }
    ctx.fetchSidebarCounts()
  }

  ctx.openInitialRouteFromUrl = async function openInitialRouteFromUrl(overrides = {}) {
    const params = new URLSearchParams(window.location.search)
    // Prefer server-rendered data attributes (passed via overrides) — after a
    // Turbo redirect from /emails/threads/:id, window.location can still briefly
    // reflect the previous page when Stimulus connect() runs.
    const threadId = overrides.threadId || params.get('thread_id')
    const folder = overrides.folder || params.get('folder')
    const view = overrides.view || params.get('view')
    const compose = overrides.compose || params.get('compose')

    let mailboxParam = null
    if (overrides.hasMailboxParam === true) {
      mailboxParam = overrides.mailboxId == null ? '' : String(overrides.mailboxId)
    } else if (overrides.hasMailboxParam !== false && params.has('mailbox_id')) {
      mailboxParam = params.get('mailbox_id')
    }

    // Legacy personal_mailbox_id query param is ignored — peers use mailbox_id.

    const parsedThreadId = threadId ? Number(threadId) : null
    const threadOnly = view === 'thread' && parsedThreadId && !Number.isNaN(parsedThreadId)

    ctx._suppressUrlSync = true
    try {
      ctx.batch(() => {
        if (folder) ctx.activeFolder.value = folder
        if (mailboxParam !== null) {
          ctx.activeMailbox.value = mailboxParam === '' ? null : Number(mailboxParam)
        }
        ctx.threadOnlyView.value = !!threadOnly
      })

      // Open the deep-linked thread immediately (don't wait for the list fetch)
      // so the reading pane shows a spinner → thread even if the row isn't in inbox.
      // Await so compose=reply can build the quote from loaded items (#335).
      if (parsedThreadId && !Number.isNaN(parsedThreadId)) {
        await ctx.selectThread(parsedThreadId)
      }

      // Thread-only popup: skip the inbox list fetch — only the open thread matters.
      if (!threadOnly) {
        await ctx.fetchEmails(1)
        // Re-assert selection after the list loads (in case a concurrent folder/mailbox
        // mutator cleared it — should be rare, but keeps deep links reliable).
        if (parsedThreadId && !Number.isNaN(parsedThreadId) && ctx.currentThreadId.value !== parsedThreadId) {
          await ctx.selectThread(parsedThreadId)
        }
        // HTML shell already embeds mailbox-scoped sidebar_counts when mailbox_id is in
        // the URL — skip the redundant GET /sidebar_counts unless bootstrap had no payload.
        if (mailboxParam !== null) {
          const boot = ctx.sidebarCounts.value
          if (!boot || Object.keys(boot).length === 0) {
            ctx.fetchSidebarCounts()
          }
        }

        if (view === 'rules') {
          ctx.openRules(ctx.activeMailbox.value)
        }
      }

      const composeType = ['new', 'reply', 'reply_all', 'forward'].includes(compose) ? compose : null
      if (composeType === 'new') {
        await ctx.openCompose('new')
      } else if (composeType && parsedThreadId && !Number.isNaN(parsedThreadId)) {
        await ctx.openCompose(composeType, parsedThreadId)
      }
    } finally {
      ctx._suppressUrlSync = false
      // Normalize the address bar to the resolved store state (bookmarkable).
      ctx.syncInboxUrl({ replace: true })
    }
  }

}
